import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, RotateCcw } from "lucide-react";
import { authService } from "../services/auth.service";
import { SuccessModal } from "./ui/success-modal";

export function ResetPasswordCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromURL = params.get("email");
    const codeFromURL = params.get("code");

    if (emailFromURL) {
      setEmail(emailFromURL);
    } else if (location.state?.email) {
      setEmail(location.state.email);
    } else {
      navigate("/forgot");
    }

    if (codeFromURL) {
      setCode(codeFromURL);
    }
  }, [location, navigate]);


  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer, canResend]);

  const isStrongPassword = (pw: string) => {
    if (pw.length < 8 || pw.length > 128) return false;
    const hasLower = /[a-z]/.test(pw);
    const hasUpper = /[A-Z]/.test(pw);
    const hasNumber = /\d/.test(pw);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw);
    return hasLower && hasUpper && hasNumber && hasSpecial;
  };

  const handleResendCode = async () => {
    if (!email) return;

    setResendLoading(true);
    try {
      await authService.forgotPassword(email);
      setResendTimer(30);
      setCanResend(false);
      setError("");
    } catch (err: any) {
      setError(err.message || "Erro ao reenviar código");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/login");
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!code || !password || !confirmPassword) {
      setError("Preencha todos os campos.");
      return;
    }
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError("O código deve ter exatamente 6 dígitos numéricos.");
      return;
    }
    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }
    if (!isStrongPassword(password)) {
      setError("A senha deve ter entre 8-128 caracteres, incluindo maiúscula, minúscula, número e caractere especial.");
      return;
    }
    if (password.includes(email.split('@')[0])) {
      setError("A senha não pode conter seu nome de usuário.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await authService.resetPassword({
        email,
        code,
        newPassword: password
      });

      setShowSuccessModal(true);
    } catch (err: any) {
      setError(err.message || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--background) 0%, var(--muted) 100%)', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 900, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', borderRadius: 20, overflow: 'hidden', background: 'var(--card)', animation: 'slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', flexDirection: 'row', minHeight: 650 }}>
          <div style={{ flex: 3, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: 48, overflow: 'hidden' }}>
            <div style={{ textAlign: 'center', color: 'white', position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: 40 }}>
                <div style={{ width: 120, height: 120, margin: '0 auto 32px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', animation: 'float 3s ease-in-out infinite' }}>
                  <Shield style={{ width: 60, height: 60 }} strokeWidth={2} />
                </div>
              </div>
              <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 20, letterSpacing: '-1px' }}>Redefinir Senha</h1>
              <p style={{ fontSize: 18, opacity: 0.9, lineHeight: 1.6, maxWidth: 400, margin: '0 auto 24px' }}>
                Informe o código recebido por e-mail e defina uma nova senha forte para continuar.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 15, opacity: 0.8, background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: 12, backdropFilter: 'blur(10px)' }}>
                <Lock style={{ width: 20, height: 20 }} />
                <span>Processo seguro e criptografado</span>
              </div>
            </div>
            <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite' }}></div>
            <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite 1s' }}></div>
          </div>
          <div style={{ flex: 2, background: 'var(--card)' }}>
            <div style={{ padding: 48, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--card-foreground)', marginBottom: 12, letterSpacing: '-0.5px' }}>Redefinir Senha</h2>
                <p style={{ color: 'var(--muted-foreground)', fontSize: 15 }}>Informe o código recebido e sua nova senha</p>
              </div>
              <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label htmlFor="code" style={{ color: 'var(--card-foreground)', fontWeight: 600, fontSize: 14 }}>Código recebido</label>
                  <input
                    id="code"
                    type="text"
                    placeholder="Digite o código"
                    value={code}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setCode(value);
                    }}
                    style={{ background: 'var(--input)', border: '2px solid var(--border)', borderRadius: 10, padding: '14px 16px', fontSize: 15, transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', outline: 'none', letterSpacing: '0.2em', textAlign: 'center', fontWeight: 600 }}
                    maxLength={6}
                    minLength={6}
                    pattern="[0-9]{6}"
                    autoComplete="one-time-code"
                    required
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, marginTop: 4, alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: 'var(--muted-foreground)', marginBottom: 2, textAlign: 'center', width: '100%' }}>Não recebeu o código?</span>
                    <button
                      type="button"
                      style={{ fontSize: 13, color: canResend ? 'var(--primary)' : 'var(--muted-foreground)', background: 'none', border: 'none', cursor: canResend ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 4, alignSelf: 'center', fontWeight: 600, transition: 'all 0.2s' }}
                      onClick={handleResendCode}
                      disabled={!canResend || resendLoading}
                      onMouseEnter={(e) => {
                        if (canResend) e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      <RotateCcw style={{ width: 14, height: 14 }} />
                      {resendLoading ? "Reenviando..." : canResend ? "Reenviar código" : `Reenviar em ${resendTimer}s`}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label htmlFor="password" style={{ color: 'var(--card-foreground)', fontWeight: 600, fontSize: 14 }}>Nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Digite sua nova senha"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      style={{ background: 'var(--input)', border: '2px solid var(--border)', borderRadius: 10, padding: '14px 48px 14px 16px', fontSize: 15, width: '100%', boxSizing: 'border-box', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', outline: 'none' }}
                      required
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'var(--primary)';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--card-foreground)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--muted-foreground)'}
                    >
                      {showPassword ? <EyeOff style={{ width: 20, height: 20 }} /> : <Eye style={{ width: 20, height: 20 }} />}
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: -4 }}>
                    Mínimo 8 caracteres com maiúscula, minúscula, número e símbolo
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label htmlFor="confirmPassword" style={{ color: 'var(--card-foreground)', fontWeight: 600, fontSize: 14 }}>Confirme a nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirme sua nova senha"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      style={{ background: 'var(--input)', border: '2px solid var(--border)', borderRadius: 10, padding: '14px 48px 14px 16px', fontSize: 15, width: '100%', boxSizing: 'border-box', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', outline: 'none' }}
                      required
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'var(--primary)';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--card-foreground)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--muted-foreground)'}
                    >
                      {showConfirmPassword ? <EyeOff style={{ width: 20, height: 20 }} /> : <Eye style={{ width: 20, height: 20 }} />}
                    </button>
                  </div>
                </div>
                {error && <div style={{ color: '#ef4444', fontSize: 14, fontWeight: 500, background: 'rgba(239, 68, 68, 0.1)', padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.2)' }}>{error}</div>}
                <button
                  type="submit"
                  style={{ width: '100%', background: loading ? 'var(--muted)' : 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)', color: 'white', fontWeight: 700, padding: '14px 24px', border: 'none', borderRadius: 10, fontSize: 16, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : '0 4px 14px rgba(59, 130, 246, 0.4)', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.4)';
                    }
                  }}
                >
                  {loading ? "Redefinindo..." : "Redefinir senha"}
                </button>
              </form>
              <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--muted-foreground)', marginTop: 32 }}>
                Lembrou da senha?{' '}
                <button
                  style={{ color: 'var(--primary)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
                  type="button"
                  onClick={() => navigate('/login')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Voltar ao login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        title="Senha Redefinida!"
        message="Sua senha foi redefinida com sucesso! Agora você pode fazer login com sua nova senha."
        onClose={handleSuccessModalClose}
        autoClose={true}
        autoCloseDelay={4000}
      />

      <style>
        {`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-20px);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.15;
            }
          }
        `}
      </style>
    </div>
  );
}
